import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, Download, Mail, Building2, Copy, Check, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Bank details - these should ideally come from backend/CMS
const BANK_DETAILS = {
  accountName: 'KBC Psychotherapy Ltd',
  sortCode: '00-00-00',
  accountNumber: '12345678'
};

const BookingSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [appointmentId, setAppointmentId] = useState(null);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const sessionId = searchParams.get('session_id');
  const paymentMethod = searchParams.get('payment_method');
  const appointmentIdParam = searchParams.get('appointment_id');
  const maxAttempts = 5;

  useEffect(() => {
    if (paymentMethod === 'bank_transfer' && appointmentIdParam && token) {
      // Bank transfer flow - fetch appointment details
      fetchAppointmentDetails(appointmentIdParam);
    } else if (sessionId && token) {
      // Stripe flow - poll payment status
      pollPaymentStatus();
    }
  }, [sessionId, token, paymentMethod, appointmentIdParam]);

  const fetchAppointmentDetails = async (aptId) => {
    try {
      const response = await axios.get(`${API}/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const apt = response.data.find(a => a.id === aptId);
      if (apt) {
        setAppointmentDetails(apt);
        setAppointmentId(aptId);
        setStatus('bank_transfer');
      } else {
        setStatus('success'); // Fallback
      }
    } catch (error) {
      console.error('Error fetching appointment:', error);
      setStatus('success'); // Fallback to generic success
    }
  };

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Try to get appointment ID from transaction if available
        if (response.data.appointment_id) {
          setAppointmentId(response.data.appointment_id);
        }
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Generate payment reference from appointment ID
  const paymentReference = appointmentId ? `KBC-${appointmentId.slice(0, 8).toUpperCase()}` : '';

  return (
    <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-6" data-testid="booking-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md text-center"
      >
        {status === 'checking' && (
          <div className="bg-card rounded-2xl shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] p-12">
            <Loader2 className="h-16 w-16 text-primary mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-3xl font-light mb-2">Confirming Payment</h1>
            <p className="text-muted-foreground">Please wait while we verify your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-card rounded-2xl shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] p-12">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl font-light mb-2">Booking Confirmed!</h1>
            <p className="text-muted-foreground mb-4">
              Thank you for your booking. We&apos;ve sent a confirmation to your email address.
            </p>
            
            {/* Invoice Notification */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8">
              <div className="flex items-center gap-3 text-green-700">
                <Mail className="h-5 w-5" />
                <p className="text-sm font-medium">
                  A PDF invoice has been sent to your email
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              <Button
                className="w-full rounded-full"
                onClick={() => navigate('/dashboard')}
                data-testid="view-appointments-btn"
              >
                View My Appointments
              </Button>
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => navigate('/intake-form')}
                data-testid="complete-intake-btn"
              >
                Complete Intake Form
              </Button>
            </div>
          </div>
        )}

        {status === 'bank_transfer' && (
          <div className="bg-card rounded-2xl shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] p-8 md:p-12 max-w-lg">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Building2 className="h-10 w-10 text-amber-600" />
            </div>
            <h1 className="font-heading text-3xl font-light mb-2">Booking Reserved!</h1>
            <p className="text-muted-foreground mb-6">
              Your appointment has been reserved. Please complete payment via bank transfer.
            </p>

            {/* Appointment Details */}
            {appointmentDetails && (
              <div className="bg-secondary/50 rounded-xl p-4 mb-6 text-left">
                <h3 className="font-medium mb-2">Appointment Details</h3>
                <p className="text-sm text-muted-foreground">
                  <strong>Service:</strong> {appointmentDetails.service_name}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Date:</strong> {formatDate(appointmentDetails.start_time)}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Amount:</strong> £{appointmentDetails.amount || appointmentDetails.service_price}
                </p>
              </div>
            )}
            
            {/* Bank Details */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6 text-left">
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Bank Transfer Details
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Account Name</p>
                    <p className="font-medium">{BANK_DETAILS.accountName}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(BANK_DETAILS.accountName, 'name')}
                    className="p-2 hover:bg-secondary rounded-lg transition-colors"
                  >
                    {copiedField === 'name' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-muted-foreground" />}
                  </button>
                </div>

                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Sort Code</p>
                    <p className="font-medium font-mono">{BANK_DETAILS.sortCode}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(BANK_DETAILS.sortCode.replace(/-/g, ''), 'sort')}
                    className="p-2 hover:bg-secondary rounded-lg transition-colors"
                  >
                    {copiedField === 'sort' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-muted-foreground" />}
                  </button>
                </div>

                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Account Number</p>
                    <p className="font-medium font-mono">{BANK_DETAILS.accountNumber}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(BANK_DETAILS.accountNumber, 'account')}
                    className="p-2 hover:bg-secondary rounded-lg transition-colors"
                  >
                    {copiedField === 'account' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-muted-foreground" />}
                  </button>
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground">Payment Reference</p>
                    <p className="font-medium font-mono text-primary">{paymentReference}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(paymentReference, 'ref')}
                    className="p-2 hover:bg-secondary rounded-lg transition-colors"
                  >
                    {copiedField === 'ref' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-muted-foreground" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-start gap-2">
                <Clock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Important</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Please use the payment reference above when making your transfer. 
                    Complete payment at least 24 hours before your appointment to confirm your booking.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => navigate('/intake-form')}
                data-testid="complete-intake-btn"
              >
                Complete Intake Form
              </Button>
              <Button
                className="w-full rounded-full"
                onClick={() => navigate('/dashboard')}
                data-testid="view-appointments-btn"
              >
                View My Appointments
              </Button>
            </div>
          </div>
        )}

        {status === 'expired' && (
          <div className="bg-card rounded-2xl shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] p-12">
            <h1 className="font-heading text-3xl font-light mb-2">Session Expired</h1>
            <p className="text-muted-foreground mb-8">
              Your payment session has expired. Please try booking again.
            </p>
            <Button
              className="w-full rounded-full"
              onClick={() => navigate('/book')}
              data-testid="try-again-btn"
            >
              Book Again
            </Button>
          </div>
        )}

        {status === 'timeout' && (
          <div className="bg-card rounded-2xl shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] p-12">
            <h1 className="font-heading text-3xl font-light mb-2">Payment Status Unknown</h1>
            <p className="text-muted-foreground mb-8">
              We couldn&apos;t verify your payment status. Please check your email for confirmation 
              or contact us if you need assistance.
            </p>
            <div className="space-y-3">
              <Button
                className="w-full rounded-full"
                onClick={() => navigate('/dashboard')}
                data-testid="check-dashboard-btn"
              >
                Check My Appointments
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default BookingSuccessPage;
